/**
 * /showme — code-gated vendor-API Q&A + Kit Builder.
 *
 * Modes:
 *   • Ask         — multi-turn chat grounded on OpenAPI + .md notes (default)
 *   • Kit Builder — goal → GPT technical draft → user UI ideas → exportable
 *                   markdown brief to paste into another coding agent
 *
 * Theme: light by default; persisted toggle in localStorage. Tailwind class
 * dark-mode (darkMode: ["class"] in tailwind.config.js).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import JSZip from 'jszip';
import {
  Lock,
  Send,
  Loader2,
  AlertTriangle,
  Sparkles,
  FileText,
  ChevronDown,
  ChevronRight,
  Trash2,
  LogOut,
  Sun,
  Moon,
  MessageSquare,
  Wrench,
  Copy,
  Download,
  Check,
  Wand2,
  Paperclip,
  X,
  Eye,
  History,
  User,
  Upload,
  Trash2 as TrashIcon,
  KeyRound,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  readStoredSession,
  clearSession,
  readStoredName,
  storeName,
  clearName,
  unlock,
  streamAsk,
  streamKitDraft,
  listSources,
  listLogs,
  uploadOpenapi,
  uploadMarkdown,
  deleteMarkdown,
} from '../../lib/api/showmeApi';

const THEME_KEY = 'showme:theme';

function readStoredTheme() {
  const t = localStorage.getItem(THEME_KEY);
  return t === 'dark' ? 'dark' : 'light';
}

// ============================================================================
// Name gate (after access code, before chat) — captured for the activity log
// ============================================================================
function NameGate({ theme, onSubmit }) {
  const [name, setName] = useState('');

  const submit = (e) => {
    e?.preventDefault();
    const trimmed = name.trim().slice(0, 60);
    if (!trimmed) return;
    storeName(trimmed);
    onSubmit(trimmed);
  };

  return (
    <div className={theme === 'dark' ? 'dark' : ''}>
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 flex items-center justify-center p-4">
        <form
          onSubmit={submit}
          className="w-full max-w-sm rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 backdrop-blur p-7 space-y-5 shadow-2xl"
          data-testid="showme-name-gate"
        >
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-emerald-100 dark:bg-emerald-500/10 border border-emerald-300 dark:border-emerald-500/30 flex items-center justify-center">
              <User className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-[15px] font-semibold text-slate-900 dark:text-white">Quick introduction</p>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">Your name shows up in the activity log so the team can see who asked what.</p>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-[11px] text-slate-500 dark:text-slate-400 uppercase tracking-wider">Your name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Rahul Dewan"
              maxLength={60}
              autoFocus
              className="bg-slate-50 dark:bg-slate-950 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white text-sm"
              data-testid="showme-name-gate-input"
            />
          </div>

          <Button
            type="submit"
            disabled={!name.trim()}
            className="w-full bg-emerald-600 dark:bg-emerald-500 hover:bg-emerald-500 dark:hover:bg-emerald-400 text-white dark:text-slate-950 font-semibold"
            data-testid="showme-name-gate-submit"
          >
            Continue
          </Button>

          <p className="text-[10px] text-slate-500 leading-relaxed text-center pt-1">
            Used only for the activity log on this page. Stored on this browser + your team's audit DB.
          </p>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// Code gate
// ============================================================================
function CodeGate({ onUnlock, theme }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setError('');
    setBusy(true);
    try {
      const { ok, data } = await unlock(code);
      if (!ok) {
        setError(data?.detail || 'Invalid code.');
        return;
      }
      onUnlock();
    } catch (err) {
      setError(err.message || 'Unlock failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={theme === 'dark' ? 'dark' : ''}>
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 flex items-center justify-center p-4">
        <form
          onSubmit={submit}
          className="w-full max-w-sm rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 backdrop-blur p-7 space-y-5 shadow-2xl"
          data-testid="showme-gate"
        >
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-emerald-100 dark:bg-emerald-500/10 border border-emerald-300 dark:border-emerald-500/30 flex items-center justify-center">
              <Lock className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-[15px] font-semibold text-slate-900 dark:text-white">RevQ Docs &middot; Show me</p>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">Vendor-API Q&amp;A console</p>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-[11px] text-slate-500 dark:text-slate-400 uppercase tracking-wider">Access code</label>
            <Input
              type="password"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="&bull; &bull; &bull; &bull; &bull;"
              inputMode="numeric"
              autoFocus
              disabled={busy}
              className="bg-slate-50 dark:bg-slate-950 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white font-mono tracking-widest text-center text-lg"
              data-testid="showme-gate-input"
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 px-3 py-2 text-[11px] text-red-700 dark:text-red-300" data-testid="showme-gate-error">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <Button
            type="submit"
            disabled={busy || code.length < 4}
            className="w-full bg-emerald-600 dark:bg-emerald-500 hover:bg-emerald-500 dark:hover:bg-emerald-400 text-white dark:text-slate-950 font-semibold gap-2"
            data-testid="showme-gate-submit"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            Unlock for 24h
          </Button>

          <p className="text-[10px] text-slate-500 leading-relaxed text-center pt-1">
            Browser-local 24h session.<br/>Questions, kits and your name are logged to the team's audit DB.
          </p>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// Shared markdown renderer
// ============================================================================
const md_components = {
  code: ({ inline, children, ...rest }) => {
    if (inline) {
      return (
        <code className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-emerald-700 dark:text-emerald-300 text-[11.5px] font-mono" {...rest}>
          {children}
        </code>
      );
    }
    return (
      <pre className="bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg p-3 my-2 overflow-x-auto">
        <code className="text-[11.5px] font-mono text-slate-800 dark:text-slate-100 leading-relaxed">{children}</code>
      </pre>
    );
  },
  table: ({ children }) => (
    <div className="my-2 overflow-x-auto">
      <table className="text-[11.5px] border-collapse w-full">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="px-2 py-1.5 text-left border-b border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 font-semibold">{children}</th>
  ),
  td: ({ children }) => (
    <td className="px-2 py-1 border-b border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-200 align-top">{children}</td>
  ),
  h1: ({ children }) => <h3 className="text-sm font-semibold text-slate-900 dark:text-white mt-3 mb-1">{children}</h3>,
  h2: ({ children }) => <h3 className="text-sm font-semibold text-slate-900 dark:text-white mt-3 mb-1">{children}</h3>,
  h3: ({ children }) => <h4 className="text-[13px] font-semibold text-slate-800 dark:text-slate-200 mt-2 mb-1">{children}</h4>,
  // Render paragraphs as <div> (not <p>) so block-level children like <pre>
  // code fences — which react-markdown can transiently nest in a paragraph
  // while a fence is still streaming in — never violate HTML nesting rules.
  p: ({ children }) => <div className="text-[12.5px] text-slate-700 dark:text-slate-200 leading-relaxed my-1.5">{children}</div>,
  ul: ({ children }) => <ul className="list-disc list-outside ml-5 text-[12.5px] text-slate-700 dark:text-slate-200 space-y-0.5 my-1.5">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-outside ml-5 text-[12.5px] text-slate-700 dark:text-slate-200 space-y-0.5 my-1.5">{children}</ol>,
  strong: ({ children }) => <strong className="font-semibold text-slate-900 dark:text-white">{children}</strong>,
};

function AnswerBubble({ answer, citations }) {
  return (
    <div className="rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 px-4 py-3" data-testid="showme-answer">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={md_components}>
        {answer}
      </ReactMarkdown>
      {citations?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-800">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Cited endpoints</p>
          <div className="flex flex-wrap gap-1.5">
            {citations.map((c, i) => (
              <span
                key={i}
                className="text-[10.5px] font-mono px-2 py-0.5 rounded bg-emerald-100 dark:bg-emerald-500/10 border border-emerald-300 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300"
                data-testid={`showme-citation-${i}`}
              >
                {c}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Logs mode — shared activity feed (all unlocked users see the same list)
// ============================================================================
function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const diffSec = Math.max(0, (Date.now() - then) / 1000);
  if (diffSec < 60) return `${Math.round(diffSec)}s ago`;
  if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)}h ago`;
  return `${Math.round(diffSec / 86400)}d ago`;
}

function LogItem({ entry, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const isKit = entry.mode === 'kit';
  const headline = isKit
    ? (entry.title || entry.goal || '(untitled kit)')
    : entry.question;

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 overflow-hidden" data-testid={`log-item-${entry.id}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors flex items-start gap-2.5"
      >
        <div className="mt-0.5">
          {open ? <ChevronDown className="h-3.5 w-3.5 text-slate-500" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-500" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className={`text-[9.5px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${
              isKit
                ? 'bg-emerald-100 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'
            }`}>
              {isKit ? 'KIT' : 'ASK'}
            </span>
            <span className="text-[11px] font-medium text-slate-700 dark:text-slate-300 truncate">
              {entry.user_name || 'Anonymous'}
            </span>
            <span className="text-[10px] text-slate-500 dark:text-slate-500 shrink-0">{relativeTime(entry.created_at)}</span>
            {isKit && entry.image_count > 0 && (
              <span className="text-[9.5px] text-slate-500 shrink-0">· {entry.image_count} 📷</span>
            )}
          </div>
          <p className="text-[12px] text-slate-800 dark:text-slate-200 truncate">{headline}</p>
        </div>
      </button>

      {open && (
        <div className="px-3 py-3 border-t border-slate-200 dark:border-slate-800 space-y-2 bg-slate-50/60 dark:bg-slate-950/60">
          {isKit ? (
            <>
              {entry.goal && (
                <div>
                  <p className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5">Goal</p>
                  <p className="text-[11.5px] text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{entry.goal}</p>
                </div>
              )}
              {entry.ui_notes && (
                <div>
                  <p className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5">UI notes</p>
                  <p className="text-[11.5px] text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{entry.ui_notes}</p>
                </div>
              )}
              {entry.brief && (
                <div>
                  <p className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5">Lumina draft</p>
                  <div className="text-[11.5px] text-slate-700 dark:text-slate-300 max-h-72 overflow-y-auto rounded border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-2 py-1.5" style={{ scrollbarGutter: 'stable' }}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={md_components}>{entry.brief}</ReactMarkdown>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <div>
                <p className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5">Question</p>
                <p className="text-[11.5px] text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{entry.question}</p>
              </div>
              {entry.answer && (
                <div>
                  <p className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5">Answer</p>
                  <div className="text-[11.5px] text-slate-700 dark:text-slate-300 max-h-72 overflow-y-auto rounded border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-2 py-1.5" style={{ scrollbarGutter: 'stable' }}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={md_components}>{entry.answer}</ReactMarkdown>
                  </div>
                </div>
              )}
            </>
          )}

          {entry.citations?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {entry.citations.map((c, i) => (
                <span
                  key={i}
                  className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-500/10 border border-emerald-300 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300"
                >
                  {c}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LogsMode({ session, onLockOut }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const { ok, status, data } = await listLogs(session.token, 50);
      if (status === 401) {
        clearSession();
        onLockOut();
        return;
      }
      if (!ok) {
        setError(typeof data?.detail === 'string' ? data.detail : `Logs fetch failed (HTTP ${status})`);
        return;
      }
      setItems(data?.logs || []);
    } catch (err) {
      setError(err.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className="flex items-center justify-between">
        <p className="text-[11px] text-slate-500 dark:text-slate-400">
          Activity from everyone who has unlocked /showme. <span className="text-slate-400">No PHI — only OpenAPI-grounded Q&amp;A and kit briefs are recorded.</span>
        </p>
        <Button
          type="button"
          onClick={load}
          disabled={loading}
          variant="outline"
          className="h-7 text-[11.5px] gap-1.5 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300"
          data-testid="logs-refresh"
        >
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <History className="h-3 w-3" />}
          Refresh
        </Button>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 px-3 py-2 text-[11.5px] text-red-700 dark:text-red-300">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading && items.length === 0 && (
        <div className="flex items-center gap-2 text-[12px] text-slate-500 py-6 justify-center">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading activity…
        </div>
      )}

      {!loading && items.length === 0 && !error && (
        <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-800 p-6 text-center text-[12px] text-slate-500" data-testid="logs-empty">
          No activity yet. Ask a question or draft a kit to start the log.
        </div>
      )}

      <div className="space-y-2" data-testid="logs-list">
        {items.map((entry) => (
          <LogItem key={entry.id} entry={entry} />
        ))}
      </div>
    </>
  );
}

// ============================================================================

// ============================================================================
// Sources drawer
// ============================================================================
function SourcesDrawer({ token }) {
  const [open, setOpen] = useState(false);
  const [sources, setSources] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [adminPwd, setAdminPwd] = useState('');
  const [adminUnlocked, setAdminUnlocked] = useState(false);
  const [busyKind, setBusyKind] = useState(null); // 'openapi' | 'md' | 'delete:<name>'
  const [feedback, setFeedback] = useState(''); // success message
  const [error, setError] = useState('');
  const openapiInputRef = useRef(null);
  const mdInputRef = useRef(null);

  const refresh = async () => {
    const { ok, data } = await listSources(token);
    if (ok) setSources(data?.sources || []);
    setLoaded(true);
  };

  useEffect(() => {
    if (!open || loaded) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loaded, token]);

  const tryUnlockAdmin = (e) => {
    e?.preventDefault();
    setError('');
    if (!adminPwd) return;
    // We don't pre-validate the password — the backend rejects on first use.
    // But to give immediate feedback we just mark "armed" locally and let
    // the first upload either succeed or 403 (in which case we re-lock).
    setAdminUnlocked(true);
  };

  const lockAdmin = () => {
    setAdminUnlocked(false);
    setAdminPwd('');
    setFeedback('');
    setError('');
  };

  const handleOpenapiPick = () => openapiInputRef.current?.click();
  const handleMdPick = () => mdInputRef.current?.click();

  const handleUploadOpenapi = async (file) => {
    if (!file) return;
    setError(''); setFeedback(''); setBusyKind('openapi');
    try {
      const { ok, status, data } = await uploadOpenapi(file, token, adminPwd);
      if (status === 403) {
        setError('Admin password is wrong. Re-enter to retry.');
        setAdminUnlocked(false);
        setAdminPwd('');
        return;
      }
      if (!ok) {
        setError(typeof data?.detail === 'string' ? data.detail : `Upload failed (HTTP ${status})`);
        return;
      }
      setFeedback(`Replaced OpenAPI spec — ${data.paths} endpoints loaded.`);
      await refresh();
    } finally {
      setBusyKind(null);
    }
  };

  const handleUploadMd = async (file) => {
    if (!file) return;
    setError(''); setFeedback(''); setBusyKind('md');
    try {
      const { ok, status, data } = await uploadMarkdown(file, token, adminPwd);
      if (status === 403) {
        setError('Admin password is wrong. Re-enter to retry.');
        setAdminUnlocked(false);
        setAdminPwd('');
        return;
      }
      if (!ok) {
        setError(typeof data?.detail === 'string' ? data.detail : `Upload failed (HTTP ${status})`);
        return;
      }
      setFeedback(`Saved ${data.filename}.`);
      await refresh();
    } finally {
      setBusyKind(null);
    }
  };

  const handleDeleteMd = async (filename) => {
    setError(''); setFeedback(''); setBusyKind(`delete:${filename}`);
    try {
      const { ok, status, data } = await deleteMarkdown(filename, token, adminPwd);
      if (status === 403) {
        setError('Admin password is wrong. Re-enter to retry.');
        setAdminUnlocked(false);
        setAdminPwd('');
        return;
      }
      if (!ok) {
        setError(typeof data?.detail === 'string' ? data.detail : `Delete failed (HTTP ${status})`);
        return;
      }
      setFeedback(`Deleted ${filename}.`);
      await refresh();
    } finally {
      setBusyKind(null);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-[11px] text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-900 transition-colors"
        data-testid="showme-sources-toggle"
      >
        <span className="flex items-center gap-1.5">
          {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          <FileText className="h-3 w-3" />
          Grounding sources
        </span>
        <span className="text-slate-500">{loaded ? `${sources.length} files` : '…'}</span>
      </button>
      {open && (
        <div className="px-3 py-2 border-t border-slate-200 dark:border-slate-800 space-y-3" data-testid="showme-sources-list">
          {/* File list */}
          <div className="space-y-1.5">
            {sources.map((s) => (
              <div key={s.filename} className="flex items-center justify-between text-[10.5px]">
                <span className="font-mono text-slate-700 dark:text-slate-300 truncate">{s.filename}</span>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="text-slate-500">
                    <span className="px-1 py-0.5 rounded bg-slate-200 dark:bg-slate-800 text-[9.5px] uppercase tracking-wider mr-1.5">{s.kind}</span>
                    {(s.bytes / 1024).toFixed(1)}KB
                  </span>
                  {adminUnlocked && s.kind === 'markdown' && (
                    <button
                      type="button"
                      onClick={() => handleDeleteMd(s.filename)}
                      disabled={busyKind === `delete:${s.filename}`}
                      className="text-slate-400 hover:text-red-500 transition-colors p-0.5"
                      title="Delete (admin)"
                      data-testid={`source-delete-${s.filename}`}
                    >
                      {busyKind === `delete:${s.filename}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <TrashIcon className="h-3 w-3" />}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Admin section */}
          <div className="pt-2 border-t border-slate-200 dark:border-slate-800">
            {!adminUnlocked ? (
              <form onSubmit={tryUnlockAdmin} className="flex items-center gap-2" data-testid="admin-lock">
                <KeyRound className="h-3 w-3 text-slate-500 shrink-0" />
                <Input
                  type="password"
                  value={adminPwd}
                  onChange={(e) => setAdminPwd(e.target.value)}
                  placeholder="Admin password to update sources"
                  className="h-7 text-[11px] bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700"
                  data-testid="admin-password-input"
                />
                <Button
                  type="submit"
                  size="sm"
                  disabled={!adminPwd}
                  className="h-7 text-[11px] bg-slate-900 hover:bg-slate-800 dark:bg-slate-700 dark:hover:bg-slate-600 text-white shrink-0"
                  data-testid="admin-unlock"
                >
                  Unlock
                </Button>
              </form>
            ) : (
              <>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10.5px] text-emerald-700 dark:text-emerald-300 font-medium flex items-center gap-1">
                    <KeyRound className="h-3 w-3" />
                    Admin armed — upload to replace sources
                  </p>
                  <button
                    type="button"
                    onClick={lockAdmin}
                    className="text-[10px] text-slate-500 hover:text-slate-900 dark:hover:text-white underline"
                    data-testid="admin-lock-again"
                  >
                    Lock
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleOpenapiPick}
                    disabled={busyKind === 'openapi'}
                    variant="outline"
                    className="h-7 text-[11px] gap-1.5 border-slate-300 dark:border-slate-700"
                    data-testid="upload-openapi-btn"
                  >
                    {busyKind === 'openapi' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                    Replace OpenAPI JSON
                  </Button>
                  <input
                    ref={openapiInputRef}
                    type="file"
                    accept="application/json,.json"
                    hidden
                    onChange={(e) => { handleUploadOpenapi(e.target.files?.[0]); e.target.value = ''; }}
                    data-testid="upload-openapi-input"
                  />

                  <Button
                    type="button"
                    size="sm"
                    onClick={handleMdPick}
                    disabled={busyKind === 'md'}
                    variant="outline"
                    className="h-7 text-[11px] gap-1.5 border-slate-300 dark:border-slate-700"
                    data-testid="upload-md-btn"
                  >
                    {busyKind === 'md' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                    Add / replace .md
                  </Button>
                  <input
                    ref={mdInputRef}
                    type="file"
                    accept=".md,text/markdown"
                    hidden
                    onChange={(e) => { handleUploadMd(e.target.files?.[0]); e.target.value = ''; }}
                    data-testid="upload-md-input"
                  />
                </div>
                <p className="text-[10px] text-slate-500 mt-1.5">
                  Filenames are sanitised. `aria_openapi.json` is always replaced in place. .md files are saved by filename (existing names overwrite).
                </p>
              </>
            )}

            {feedback && (
              <div className="mt-2 rounded bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 px-2 py-1 text-[10.5px] text-emerald-700 dark:text-emerald-300" data-testid="admin-feedback">
                {feedback}
              </div>
            )}
            {error && (
              <div className="mt-2 rounded bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 px-2 py-1 text-[10.5px] text-red-700 dark:text-red-300" data-testid="admin-error">
                {error}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Ask mode (chat)
// ============================================================================
// Remove a trailing "CITATIONS: [...]" line (also strips a partial one that's
// still streaming in) so it never flashes in the rendered answer/brief.
function stripCitationsLine(text) {
  return String(text).replace(/\n?CITATIONS:\s*(\[[\s\S]*)?$/, '').trimEnd();
}

function AskMode({ session, onLockOut }) {
  const [history, setHistory] = useState([]);
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState(false);
  const [streaming, setStreaming] = useState('');
  const [error, setError] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history, pending, streaming]);

  const send = async () => {
    const q = draft.trim();
    if (!q || pending) return;
    setError('');
    const next = [...history, { role: 'user', content: q }];
    setHistory(next);
    setDraft('');
    setPending(true);
    setStreaming('');
    try {
      const apiHistory = next.map(({ role, content }) => ({ role, content }));
      let acc = '';
      const res = await streamAsk(q, apiHistory.slice(0, -1), session.token, (chunk) => {
        acc += chunk;
        setStreaming(acc);
      });
      if (res.status === 401) {
        clearSession();
        onLockOut();
        return;
      }
      if (!res.ok) {
        setError(typeof res.error === 'string' ? res.error : 'Request failed');
        setHistory(history);
        setDraft(q);
        return;
      }
      setHistory([
        ...next,
        { role: 'assistant', content: stripCitationsLine(acc), citations: res.citations || [] },
      ]);
    } catch (err) {
      setError(err.message || 'Network error');
      setHistory(history);
      setDraft(q);
    } finally {
      setPending(false);
      setStreaming('');
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      <SourcesDrawer token={session.token} />

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-scroll space-y-3 pr-1 min-h-[40vh]"
        style={{ scrollbarGutter: 'stable' }}
        data-testid="showme-messages"
      >
        {history.length === 0 && !pending && (
          <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-800 p-6 text-center">
            <p className="text-[13px] text-slate-700 dark:text-slate-300 mb-1.5">Ask anything about the RevQ /edi API.</p>
            <p className="text-[11px] text-slate-500 mb-4">
              Grounded on the live OpenAPI spec + curated vendor notes.
            </p>
            <div className="flex flex-wrap gap-1.5 justify-center">
              {[
                'How do I create a sub-login with claim-scrubber access only?',
                'Show me the curl to add an NPI',
                'What fields does POST /clients accept and what are the error codes?',
                'Difference between /signup/intent and /signup/finalize?',
              ].map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => setDraft(q)}
                  className="text-[10.5px] px-2 py-1 rounded-full border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-emerald-500/50 hover:text-emerald-600 dark:hover:text-emerald-300 transition-colors"
                  data-testid={`showme-suggestion-${q.slice(0, 20)}`}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {history.map((msg, i) => (
          <div key={i} className={msg.role === 'user' ? 'flex justify-end' : ''}>
            {msg.role === 'user' ? (
              <div
                className="max-w-[80%] rounded-xl bg-emerald-100 dark:bg-emerald-500/10 border border-emerald-300 dark:border-emerald-500/30 px-3 py-2 text-[12.5px] text-emerald-900 dark:text-emerald-100 whitespace-pre-wrap"
                data-testid={`showme-msg-user-${i}`}
              >
                {msg.content}
              </div>
            ) : (
              <AnswerBubble answer={msg.content} citations={msg.citations} />
            )}
          </div>
        ))}

        {pending && (
          streaming ? (
            <div data-testid="showme-streaming">
              <AnswerBubble answer={stripCitationsLine(streaming)} citations={[]} />
            </div>
          ) : (
            <div className="flex items-center gap-2 text-[12px] text-slate-500 dark:text-slate-400" data-testid="showme-pending">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Lumina is reading the docs…
            </div>
          )
        )}
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 px-3 py-2 text-[11.5px] text-red-700 dark:text-red-300" data-testid="showme-error">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 p-2">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Ask about endpoints, payloads, validation rules… (⌘/Ctrl+Enter to send)"
          disabled={pending}
          rows={3}
          className="bg-transparent border-0 text-[12.5px] text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 resize-none focus-visible:ring-0 focus-visible:ring-offset-0"
          style={{ scrollbarGutter: 'stable' }}
          data-testid="showme-input"
        />
        <div className="flex items-center justify-between mt-1">
          <p className="text-[10px] text-slate-500 pl-2">
            {history.filter((m) => m.role === 'user').length} questions in this session
          </p>
          <Button
            type="button"
            onClick={send}
            disabled={!draft.trim() || pending}
            className="bg-emerald-600 dark:bg-emerald-500 hover:bg-emerald-500 dark:hover:bg-emerald-400 text-white dark:text-slate-950 font-semibold gap-1.5 h-8 text-[12px]"
            data-testid="showme-send"
          >
            {pending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
            Ask
          </Button>
        </div>
      </div>
    </>
  );
}

// ============================================================================
// Kit Builder mode
// ============================================================================
function buildKitMarkdown({ title, goal, brief, citations, uiNotes, images, textAssets }) {
  const ts = new Date().toISOString().replace('T', ' ').slice(0, 16) + ' UTC';
  const safeTitle = title || 'New RevQ feature';
  const cites = (citations || []).length
    ? (citations || []).map((c) => `- \`${c}\``).join('\n')
    : '_(none cited)_';
  const imageRefs = (images || []).length
    ? '\n\n**Attached snapshots** (see `images/` folder in this kit):\n' +
      images.map((im) => `\n![${im.alt || im.filename}](images/${im.filename})`).join('')
    : '';
  const assetRefs = (textAssets || []).length
    ? '\n\n**Attached design files** (authoritative design intent — see `assets/` folder):\n' +
      textAssets.map((a) => `- \`assets/${a.filename}\` · ${a.size != null && a.size < 1024 ? `${a.size}B` : `${a.sizeKB}KB`}`).join('\n')
    : '';
  return `# Feature Brief — ${safeTitle}

> Generated by RevQ Docs · Show me on ${ts}
> Grounded on the live OpenAPI spec + curated vendor notes.

---

## 1. What we want to build

${goal || '_(no goal supplied)_'}

---

## 2. Technical surface (Lumina, grounded on vendor docs)

${brief || '_(no draft yet — click "Draft technical plan")_'}

### Cited endpoints

${cites}

---

## 3. UI ideas (from the product owner)

${uiNotes || '_(no UI notes supplied)_'}${imageRefs}${assetRefs}

---

## 4. Handoff checklist for the coding agent

- [ ] Add backend proxy route(s) in \`/app/backend/revqdemo_routes.py\` for each cited endpoint
- [ ] Add API wrappers in \`/app/frontend/src/lib/api/ediApi.js\`
- [ ] Create the new dialog/page component under \`/app/frontend/src/components/auth/\` or \`/app/frontend/src/pages/\`
- [ ] Wire trigger into the parent (e.g. \`RevqdemoMetricsPanel.js\`) with a \`data-testid\`
- [ ] Surface vendor error \`detail\` via \`readVendorError\` (string | array | object shapes)
- [ ] On success, refresh dependent counts via the existing \`fetchAll\` pattern
- [ ] Run \`yarn mode-separation\` and \`yarn build\` in \`/app/frontend\` before finishing
- [ ] If any auth/credentials change, update \`/app/memory/test_credentials.md\`
`;
}

// Map a file extension to a markdown code-fence language hint.
const FENCE_LANG = {
  html: 'html', htm: 'html', css: 'css', scss: 'scss', less: 'less',
  md: 'markdown', markdown: 'markdown', txt: '', js: 'javascript',
  jsx: 'jsx', ts: 'typescript', tsx: 'tsx', json: 'json', svg: 'xml',
  xml: 'xml', yaml: 'yaml', yml: 'yaml', csv: '',
};

// Pick a fence longer than any run of backticks inside the content so the
// fenced block never breaks (markdown/html files can contain ``` themselves).
function fenceFor(content) {
  const runs = String(content).match(/`+/g) || [];
  const longest = runs.reduce((m, r) => Math.max(m, r.length), 0);
  return '`'.repeat(Math.max(3, longest + 1));
}

// Build a single, self-contained prompt to paste into a fresh coding agent —
// the full feature brief PLUS the inlined contents of every text design file
// and a file tree, so the agent has the whole kit from one paste.
function buildEmergentPrompt({ kitMarkdown, textAssets, images }) {
  const assets = textAssets || [];
  const imgs = images || [];
  let out = kitMarkdown;
  if (assets.length === 0 && imgs.length === 0) return out;

  out += '\n\n---\n\n## Attached design files (authoritative design intent)\n\n';
  out += 'The raw contents of the uploaded design files are inlined below. Treat their layout, component structure, naming and styling as the intended UI. Do NOT infer API endpoints or fields from them — the API surface is defined in section 2 above.\n\n';

  out += '### File tree\n\n```\n';
  for (const a of assets) out += `${a.filename}\n`;
  for (const im of imgs) out += `${im.filename}  (image — bundled in the .zip)\n`;
  out += '```\n';

  for (const a of assets) {
    const ext = (a.filename.split('.').pop() || '').toLowerCase();
    const lang = FENCE_LANG[ext] || '';
    const fence = fenceFor(a.content);
    out += `\n### \`${a.filename}\`\n\n${fence}${lang}\n${a.content}\n${fence}\n`;
  }

  if (imgs.length) {
    out += `\n_${imgs.length} image${imgs.length === 1 ? '' : 's'} (${imgs.map((im) => im.filename).join(', ')}) are bundled in the downloadable .zip — attach them to the agent separately if visual context is needed._\n`;
  }
  return out;
}

function PendingTicker({ images = 0, assets = 0 }) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const parts = [];
  if (images > 0) parts.push(`${images} snapshot${images === 1 ? '' : 's'}`);
  if (assets > 0) parts.push(`${assets} design file${assets === 1 ? '' : 's'}`);
  const base = parts.length
    ? `Lumina is reading the docs and ${parts.join(' + ')}…`
    : 'Lumina is reading the docs…';
  let extra = '';
  if (tick >= 30) extra = ' This is taking a bit longer than usual — still working.';
  else if (tick >= 15) extra = ' Still working — vision-grounded drafts can take ~10-30s.';
  return (
    <div className="flex items-center gap-2 text-[12px] text-slate-500 dark:text-slate-400 py-4" data-testid="kit-pending">
      <Loader2 className="h-3.5 w-3.5 animate-spin" />
      <span>{base}{extra && <span className="text-slate-400 dark:text-slate-500"> ·{extra}</span>}</span>
    </div>
  );
}

// Kit Builder file ingestion config — images go to GPT-5.2 Vision (first 6),
// text files (html/css/md/svg/etc.) go in as authoritative design context.
const KIT_IMAGE_EXT = new Set(['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp']);
const KIT_TEXT_EXT = new Set([
  'md', 'markdown', 'html', 'htm', 'css', 'scss', 'less', 'txt',
  'js', 'jsx', 'ts', 'tsx', 'json', 'svg', 'xml', 'yaml', 'yml', 'csv',
]);
const KIT_VISION_MAX = 6;
const KIT_MAX_IMAGES = 30;
const KIT_MAX_ASSETS = 40;
const KIT_ZIP_MAX_BYTES = 15 * 1024 * 1024;
const KIT_IMAGE_MAX_BYTES = 4 * 1024 * 1024;
const KIT_FILE_ACCEPT =
  'image/png,image/jpeg,image/webp,image/gif,.zip,.md,.markdown,.html,.htm,.css,.scss,.less,.txt,.js,.jsx,.ts,.tsx,.json,.svg,.xml,.yaml,.yml,.csv';

const extOf = (name) => (String(name).split('.').pop() || '').toLowerCase();
const isHiddenPath = (path) =>
  String(path).split('/').some((seg) => seg.startsWith('.') || seg === '__MACOSX');
const fileToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });

function KitMode({ session, onLockOut }) {
  const [title, setTitle] = useState('');
  const [goal, setGoal] = useState('');
  const [uiNotes, setUiNotes] = useState('');
  const [images, setImages] = useState([]); // [{id, filename, mime, dataUrl, sizeKB, alt}]
  const [textAssets, setTextAssets] = useState([]); // [{id, filename, content, sizeKB}]
  const [brief, setBrief] = useState('');
  const [citations, setCitations] = useState([]);
  const [drafting, setDrafting] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [copiedPrompt, setCopiedPrompt] = useState(false);
  const [previewIdx, setPreviewIdx] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const fileInputRef = useRef(null);
  const briefRef = useRef(null);
  const outputRef = useRef(null);

  const handlePickFiles = () => fileInputRef.current?.click();

  // Ingest images, text files, and .zip bundles. Images flow to GPT-5.2 Vision
  // (first 6) + the export bundle; text files (html/css/md/svg/etc.) flow in as
  // authoritative design context + an assets/ folder in the export zip.
  const ingestFiles = async (fileList) => {
    setError('');
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    const newImages = [];
    const newAssets = [];
    const notes = [];
    let imgCount = images.length;
    let assetCount = textAssets.length;

    const addImageFromBlob = async (blob, name) => {
      if (imgCount >= KIT_MAX_IMAGES) { notes.push(`image limit (${KIT_MAX_IMAGES}) reached`); return; }
      if (blob.size > KIT_IMAGE_MAX_BYTES) { notes.push(`"${name}" image over 4MB skipped`); return; }
      const dataUrl = await fileToDataUrl(blob);
      imgCount += 1;
      const ext = extOf(name) || 'png';
      newImages.push({
        id: `${Date.now()}-img-${imgCount}-${Math.random().toString(36).slice(2, 6)}`,
        filename: `ui-${imgCount}.${ext}`,
        mime: blob.type || `image/${ext}`,
        dataUrl,
        sizeKB: Math.round(blob.size / 1024),
        alt: String(name).replace(/\.[^.]+$/, ''),
      });
    };

    const addTextAsset = (content, name, size) => {
      if (assetCount >= KIT_MAX_ASSETS) { notes.push(`file limit (${KIT_MAX_ASSETS}) reached`); return; }
      assetCount += 1;
      newAssets.push({
        id: `${Date.now()}-asset-${assetCount}-${Math.random().toString(36).slice(2, 6)}`,
        filename: name,
        content,
        size,
        sizeKB: Math.round(size / 1024),
      });
    };

    try {
      for (const f of files) {
        const ext = extOf(f.name);
        const isZip = ext === 'zip' || f.type === 'application/zip' || f.type === 'application/x-zip-compressed';
        if (isZip) {
          if (f.size > KIT_ZIP_MAX_BYTES) { notes.push(`"${f.name}" zip over 15MB skipped`); continue; }
          // eslint-disable-next-line no-await-in-loop
          const zip = await JSZip.loadAsync(f);
          const entries = Object.values(zip.files)
            .filter((e) => !e.dir && !isHiddenPath(e.name))
            .sort((a, b) => a.name.localeCompare(b.name));
          for (const entry of entries) {
            const eExt = extOf(entry.name);
            const baseName = entry.name.split('/').pop();
            if (KIT_IMAGE_EXT.has(eExt)) {
              // eslint-disable-next-line no-await-in-loop
              const blob = await entry.async('blob');
              // eslint-disable-next-line no-await-in-loop
              await addImageFromBlob(blob, baseName);
            } else if (KIT_TEXT_EXT.has(eExt)) {
              // eslint-disable-next-line no-await-in-loop
              const content = await entry.async('string');
              if (content.trim()) addTextAsset(content, entry.name.replace(/^\.\//, ''), content.length);
            } else {
              notes.push(`${baseName} (unsupported) skipped`);
            }
          }
        } else if (f.type.startsWith('image/') || KIT_IMAGE_EXT.has(ext)) {
          // eslint-disable-next-line no-await-in-loop
          await addImageFromBlob(f, f.name);
        } else if (KIT_TEXT_EXT.has(ext) || f.type.startsWith('text/')) {
          // eslint-disable-next-line no-await-in-loop
          const content = await f.text();
          if (content.trim()) addTextAsset(content, f.name, f.size);
        } else {
          notes.push(`"${f.name}" (unsupported) skipped`);
        }
      }
    } catch (err) {
      setError(`Couldn't read upload: ${err.message || 'unknown error'}`);
      return;
    }

    if (newImages.length) setImages((prev) => [...prev, ...newImages]);
    if (newAssets.length) setTextAssets((prev) => [...prev, ...newAssets]);
    if (notes.length) {
      const uniq = Array.from(new Set(notes)).slice(0, 4);
      setError(uniq.join(' · '));
    }
  };

  const removeImage = (id) => setImages((prev) => prev.filter((im) => im.id !== id));
  const removeAsset = (id) => setTextAssets((prev) => prev.filter((a) => a.id !== id));

  // Trim text-asset payload before sending to the model (per-file + total cap).
  const buildAssetPayload = () => {
    const PER = 40_000;
    const TOTAL = 250_000;
    const out = [];
    let total = 0;
    for (const a of textAssets) {
      if (total >= TOTAL) break;
      let snippet = a.content.slice(0, PER);
      if (total + snippet.length > TOTAL) snippet = snippet.slice(0, TOTAL - total);
      if (!snippet) break;
      out.push({ filename: a.filename, content: snippet });
      total += snippet.length;
    }
    return out;
  };

  const handleDraft = async () => {
    if (!goal.trim() || drafting) return;
    setError('');
    setDrafting(true);
    setBrief('');
    setCitations([]);
    setShowPreview(false);
    // Jump the user to the live draft so they watch it stream in.
    setTimeout(() => briefRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 120);
    try {
      const imageDataUrls = images.slice(0, KIT_VISION_MAX).map((im) => im.dataUrl);
      const textAssetPayload = buildAssetPayload();
      let acc = '';
      const res = await streamKitDraft(goal, uiNotes, imageDataUrls, title, textAssetPayload, session.token, (chunk) => {
        acc += chunk;
        setBrief(stripCitationsLine(acc));
      });
      if (res.status === 401) {
        clearSession();
        onLockOut();
        return;
      }
      if (!res.ok) {
        setError(typeof res.error === 'string' ? res.error : 'Draft failed');
        return;
      }
      setBrief(stripCitationsLine(acc));
      setCitations(res.citations || []);
      // Land the user on the now-unlocked "Ready to paste" export section.
      setTimeout(() => outputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
    } catch (err) {
      setError(err.message || 'Network error');
    } finally {
      setDrafting(false);
    }
  };

  const kitMarkdown = useMemo(
    () => buildKitMarkdown({ title, goal, brief, citations, uiNotes, images, textAssets }),
    [title, goal, brief, citations, uiNotes, images, textAssets],
  );

  const emergentPrompt = useMemo(
    () => buildEmergentPrompt({ kitMarkdown, textAssets, images }),
    [kitMarkdown, textAssets, images],
  );

  const slugTitle = () =>
    (title || 'feature-brief')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) || 'feature-brief';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(kitMarkdown);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      setError('Copy failed — your browser blocked clipboard access.');
    }
  };

  const handleCopyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(emergentPrompt);
      setCopiedPrompt(true);
      setTimeout(() => setCopiedPrompt(false), 1500);
    } catch (err) {
      setError('Copy failed — your browser blocked clipboard access.');
    }
  };

  const handleDownload = async () => {
    const slug = slugTitle();
    if (images.length === 0 && textAssets.length === 0) {
      // Plain .md
      const blob = new Blob([kitMarkdown], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${slug}.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      return;
    }
    // Zip with markdown + the paste-ready agent prompt + images/ + assets/
    const zip = new JSZip();
    zip.file('brief.md', kitMarkdown);
    zip.file('EMERGENT_PROMPT.md', emergentPrompt);
    if (images.length) {
      const imagesFolder = zip.folder('images');
      for (const im of images) {
        // strip the "data:image/xxx;base64," prefix
        const base64 = (im.dataUrl.split(',')[1]) || '';
        imagesFolder.file(im.filename, base64, { base64: true });
      }
    }
    if (textAssets.length) {
      const assetsFolder = zip.folder('assets');
      for (const a of textAssets) {
        assetsFolder.file(a.filename, a.content);
      }
    }
    const blob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${slug}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <SourcesDrawer token={session.token} />

      <div className="space-y-3" data-testid="showme-kit">
        {/* Title */}
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 p-3 space-y-1.5">
          <label className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-medium">
            Feature title <span className="text-slate-400 lowercase tracking-normal font-normal">(short, e.g. "Bulk roster CSV upload")</span>
          </label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Bulk roster CSV upload"
            disabled={drafting}
            className="text-sm bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100 disabled:opacity-60"
            data-testid="kit-title"
          />
        </div>

        {/* Goal */}
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 p-3 space-y-1.5">
          <label className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-medium">
            1 · What do you want to achieve?
          </label>
          <Textarea
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            placeholder="Plain English. Who's the user, what action are they taking, what should they see when it works?  Example: 'A primary user uploads a CSV of providers to a billing profile. The system parses it, shows them a preview, and they confirm to apply.'"
            rows={5}
            disabled={drafting}
            className="text-[12.5px] bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100 resize-y placeholder:text-slate-400 dark:placeholder:text-slate-500 disabled:opacity-60"
            style={{ scrollbarGutter: 'stable' }}
            data-testid="kit-goal"
          />
          <p className="text-[10px] text-slate-500 pt-1">
            Lumina uses this + your UI ideas + the vendor docs to draft the technical surface.
          </p>
        </div>

        {/* UI ideas + snapshots */}
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 p-3 space-y-1.5">
          <label className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-medium">
            2 · Your UI ideas <span className="text-slate-400 lowercase tracking-normal font-normal">(layout, components, copy, edge cases…)</span>
          </label>
          <Textarea
            value={uiNotes}
            onChange={(e) => setUiNotes(e.target.value)}
            placeholder="Free-form. Example: 'Open as a wide modal from a + button on the Billing Profiles row. Step 1: drag-drop CSV. Step 2: scrollable preview table with errors inline. Confirm button at bottom only enables when zero errors. Match the AddBillingProfileDialog visual style.'"
            rows={5}
            disabled={drafting}
            className="text-[12.5px] bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100 resize-y placeholder:text-slate-400 dark:placeholder:text-slate-500 disabled:opacity-60"
            style={{ scrollbarGutter: 'stable' }}
            data-testid="kit-ui-notes"
          />

          {/* Uploads row — images + .zip of design files */}
          <div className="flex items-center justify-between pt-2 gap-3">
            <p className="text-[10px] text-slate-500">
              {images.length === 0 && textAssets.length === 0
                ? 'Upload wireframes / screenshots, or a .zip of HTML / CSS / .md design files — Lumina reads them as authoritative design intent.'
                : `${images.length} image${images.length === 1 ? '' : 's'}${images.length > KIT_VISION_MAX ? ` (first ${KIT_VISION_MAX} sent to vision)` : ''} · ${textAssets.length} design file${textAssets.length === 1 ? '' : 's'} attached · bundled into the zip handoff`}
            </p>
            <Button
              type="button"
              onClick={handlePickFiles}
              disabled={drafting}
              variant="outline"
              className="gap-1.5 h-7 text-[11.5px] border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 shrink-0 disabled:opacity-60"
              data-testid="kit-add-files"
            >
              <Paperclip className="h-3 w-3" />
              Add files / .zip
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept={KIT_FILE_ACCEPT}
              multiple
              hidden
              onChange={(e) => { ingestFiles(e.target.files); e.target.value = ''; }}
              data-testid="kit-file-input"
            />
          </div>

          {textAssets.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-2" data-testid="kit-assets">
              {textAssets.map((a, i) => (
                <div
                  key={a.id}
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 px-2 py-1 text-[10.5px] font-mono text-slate-600 dark:text-slate-300"
                  data-testid={`kit-asset-${i}`}
                >
                  <FileText className="h-3 w-3 text-slate-400 shrink-0" />
                  <span className="truncate max-w-[180px]" title={a.filename}>{a.filename}</span>
                  <span className="text-slate-400">· {a.size < 1024 ? `${a.size}B` : `${a.sizeKB}KB`}</span>
                  <button
                    type="button"
                    onClick={() => removeAsset(a.id)}
                    disabled={drafting}
                    className="ml-0.5 text-slate-400 hover:text-red-500 transition-colors disabled:opacity-40"
                    aria-label={`Remove ${a.filename}`}
                    data-testid={`kit-asset-remove-${i}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {images.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-2" data-testid="kit-thumbnails">
              {images.map((im, i) => (
                <div
                  key={im.id}
                  className="relative group rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 overflow-hidden w-24 h-24 shrink-0"
                  data-testid={`kit-thumb-${i}`}
                >
                  <img src={im.dataUrl} alt={im.alt} className="w-full h-full object-cover" />
                  {i >= KIT_VISION_MAX && (
                    <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-amber-500/90 text-white text-[8px] font-semibold uppercase tracking-wide" data-testid={`kit-thumb-bundleonly-${i}`}>
                      bundle only
                    </div>
                  )}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-1.5 py-1 text-[9px] text-white font-mono truncate">
                    {im.filename} · {im.sizeKB}KB
                  </div>
                  <button
                    type="button"
                    onClick={() => setPreviewIdx(i)}
                    className={`absolute h-5 w-5 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity ${i >= KIT_VISION_MAX ? 'top-1 right-7' : 'top-1 left-1'}`}
                    aria-label="Preview"
                    data-testid={`kit-thumb-preview-${i}`}
                  >
                    <Eye className="h-3 w-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeImage(im.id)}
                    disabled={drafting}
                    className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/60 hover:bg-red-500 text-white flex items-center justify-center transition-colors disabled:opacity-40"
                    aria-label="Remove"
                    data-testid={`kit-thumb-remove-${i}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Draft CTA — disables inputs above + streams the plan below */}
        <Button
          type="button"
          onClick={handleDraft}
          disabled={!goal.trim() || drafting}
          className="w-full h-11 bg-slate-900 hover:bg-slate-800 dark:bg-emerald-500 dark:hover:bg-emerald-400 text-white dark:text-slate-950 gap-2 text-[13px] font-semibold"
          data-testid="kit-draft-btn"
        >
          {drafting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
          {drafting ? 'Drafting…' : (brief ? 'Re-draft plan with edits' : 'Draft technical plan')}
        </Button>

        {/* Draft preview */}
        {(brief || drafting) && (
          <div ref={briefRef} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 p-3 space-y-2" data-testid="kit-brief">
            <div className="flex items-center justify-between gap-2">
              <p className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-medium">
                3 · Lumina technical draft
              </p>
              {drafting ? (
                <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-amber-600 dark:text-amber-400" data-testid="kit-draft-status">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" /> Generating…
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400" data-testid="kit-draft-status">
                  <Check className="h-3 w-3" /> Draft ready
                </span>
              )}
            </div>
            {drafting && !brief ? (
              <PendingTicker images={Math.min(images.length, KIT_VISION_MAX)} assets={textAssets.length} />
            ) : (
              <>
                <div className="max-h-[60vh] overflow-y-auto rounded-lg bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 px-3 py-2" style={{ scrollbarGutter: 'stable' }}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={md_components}>{brief}</ReactMarkdown>
                </div>
                {!drafting && citations.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {citations.map((c, i) => (
                      <span
                        key={i}
                        className="text-[10.5px] font-mono px-2 py-0.5 rounded bg-emerald-100 dark:bg-emerald-500/10 border border-emerald-300 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300"
                        data-testid={`kit-citation-${i}`}
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 px-3 py-2 text-[11.5px] text-red-700 dark:text-red-300" data-testid="kit-error">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Final export — lights up only when the draft is ready */}
        <div
          ref={outputRef}
          className={`rounded-xl border-2 p-3 space-y-2 transition-all duration-500 ${
            (brief && !drafting)
              ? 'border-emerald-400 dark:border-emerald-500/60 bg-emerald-50/60 dark:bg-emerald-500/10 shadow-[0_0_0_3px_rgba(16,185,129,0.18)]'
              : 'border-dashed border-slate-300 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/20 opacity-70'
          }`}
          data-testid="kit-output"
        >
          <div className="flex items-center justify-between gap-2">
            <p className={`text-[11px] uppercase tracking-wider font-semibold ${(brief && !drafting) ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-400 dark:text-slate-500'}`}>
              4 · Ready to paste into Emergent {(brief && !drafting && (images.length > 0 || textAssets.length > 0)) ? '(zip with files)' : ''}
            </p>
            {(brief && !drafting) ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300" data-testid="kit-ready-badge">
                <Check className="h-3 w-3" /> Ready
              </span>
            ) : (
              <span className="text-[10px] text-slate-400 dark:text-slate-500" data-testid="kit-ready-hint">
                {drafting ? 'Generating…' : 'Draft a plan to unlock export'}
              </span>
            )}
          </div>

          {(brief && !drafting) && (
            <>
              <div className="flex items-center gap-1.5 flex-wrap">
                <Button
                  type="button"
                  size="sm"
                  onClick={handleDownload}
                  className="bg-emerald-600 hover:bg-emerald-500 dark:bg-emerald-500 dark:hover:bg-emerald-400 text-white dark:text-slate-950 gap-1.5 h-7 text-[11.5px]"
                  data-testid="kit-download"
                >
                  <Download className="h-3 w-3" />
                  {(images.length > 0 || textAssets.length > 0) ? 'Download .zip' : 'Download .md'}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleCopyPrompt}
                  title="Copies the full brief + every design file inlined — paste straight into a new Emergent agent"
                  className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800 gap-1.5 h-7 text-[11.5px]"
                  data-testid="kit-copy-prompt"
                >
                  {copiedPrompt ? <Check className="h-3 w-3 text-emerald-600" /> : <Sparkles className="h-3 w-3" />}
                  {copiedPrompt ? 'Copied' : 'Copy as Emergent prompt'}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleCopy}
                  className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800 gap-1.5 h-7 text-[11.5px]"
                  data-testid="kit-copy"
                >
                  {copied ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
                  {copied ? 'Copied' : 'Copy markdown'}
                </Button>
                <button
                  type="button"
                  onClick={() => setShowPreview((v) => !v)}
                  className="ml-auto text-[10.5px] text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 underline underline-offset-2"
                  data-testid="kit-toggle-preview"
                >
                  {showPreview ? 'Hide raw markdown' : 'Show raw markdown'}
                </button>
              </div>
              {showPreview && (
                <div className="max-h-[55vh] overflow-y-auto rounded-lg bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 px-3 py-2" style={{ scrollbarGutter: 'stable' }}>
                  <pre className="text-[11px] font-mono text-slate-700 dark:text-slate-300 whitespace-pre-wrap" data-testid="kit-preview">
                    {kitMarkdown}
                  </pre>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Lightbox preview */}
      {previewIdx !== null && images[previewIdx] && (
        <div
          className="fixed inset-0 z-[2000] bg-black/85 flex items-center justify-center p-6 cursor-zoom-out"
          onClick={() => setPreviewIdx(null)}
          data-testid="kit-thumb-lightbox"
        >
          <img
            src={images[previewIdx].dataUrl}
            alt={images[previewIdx].alt}
            className="max-w-[95vw] max-h-[95vh] rounded-lg shadow-2xl"
          />
        </div>
      )}
    </>
  );
}

// ============================================================================
// Chat console (header + mode switcher)
// ============================================================================
function Console({ session, theme, setTheme, userName, onLockOut, onChangeName }) {
  const [mode, setMode] = useState('ask'); // 'ask' | 'kit' | 'logs'

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem(THEME_KEY, next);
  };

  const expiresIn = useMemo(
    () => Math.max(0, Math.floor((session.expires_at * 1000 - Date.now()) / 3600000)),
    [session.expires_at],
  );

  const logout = () => {
    clearSession();
    onLockOut();
  };

  return (
    <div className={theme === 'dark' ? 'dark' : ''}>
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 flex flex-col">
        {/* Header */}
        <header className="border-b border-slate-200 dark:border-slate-800 px-5 py-2.5 flex items-center justify-between bg-white/80 dark:bg-slate-900/40 backdrop-blur sticky top-0 z-10">
          <div className="flex items-center gap-2.5">
            <div className="h-7 w-7 rounded-md bg-emerald-100 dark:bg-emerald-500/10 border border-emerald-300 dark:border-emerald-500/30 flex items-center justify-center">
              <Sparkles className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <p className="text-[13px] font-semibold text-slate-900 dark:text-white leading-tight">RevQ Docs · Show me</p>
          </div>

          {/* Mode tabs */}
          <div className="flex items-center gap-1 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-900 p-0.5">
            <button
              type="button"
              onClick={() => setMode('ask')}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11.5px] font-medium transition-colors ${
                mode === 'ask'
                  ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
              data-testid="showme-mode-ask"
            >
              <MessageSquare className="h-3 w-3" />
              Ask
            </button>
            <button
              type="button"
              onClick={() => setMode('kit')}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11.5px] font-medium transition-colors ${
                mode === 'kit'
                  ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
              data-testid="showme-mode-kit"
            >
              <Wrench className="h-3 w-3" />
              Kit Builder
            </button>
            <button
              type="button"
              onClick={() => setMode('logs')}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11.5px] font-medium transition-colors ${
                mode === 'logs'
                  ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
              data-testid="showme-mode-logs"
            >
              <History className="h-3 w-3" />
              Logs
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onChangeName}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10.5px] text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              data-testid="showme-user-name"
              title="Change name"
            >
              <User className="h-3 w-3" />
              {userName || 'Anonymous'}
            </button>
            <span className="text-[10px] text-slate-500" data-testid="showme-session-ttl">~{expiresIn}h left</span>
            <button
              type="button"
              onClick={toggleTheme}
              className="inline-flex items-center gap-1 p-1.5 rounded text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              data-testid="showme-theme-toggle"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
            </button>
            <button
              type="button"
              onClick={logout}
              className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10.5px] text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              data-testid="showme-logout"
            >
              <LogOut className="h-3 w-3" />
              Lock
            </button>
          </div>
        </header>

        {/* Body */}
        <div className="flex-1 flex flex-col max-w-3xl w-full mx-auto px-4 py-4 gap-3">
          {mode === 'ask' && <AskMode session={session} onLockOut={onLockOut} />}
          {mode === 'kit' && <KitMode session={session} onLockOut={onLockOut} />}
          {mode === 'logs' && <LogsMode session={session} onLockOut={onLockOut} />}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Root
// ============================================================================
export default function ShowMePage() {
  const [session, setSession] = useState(() => readStoredSession());
  const [theme, setTheme] = useState(() => readStoredTheme());
  const [userName, setUserName] = useState(() => readStoredName());

  useEffect(() => {
    const prev = document.title;
    document.title = 'Aria One - Dev Help';
    return () => { document.title = prev; };
  }, []);

  if (!session) {
    return <CodeGate theme={theme} onUnlock={() => setSession(readStoredSession())} />;
  }
  if (!userName) {
    return <NameGate theme={theme} onSubmit={(name) => setUserName(name)} />;
  }
  return (
    <Console
      session={session}
      theme={theme}
      setTheme={setTheme}
      userName={userName}
      onChangeName={() => { clearName(); setUserName(''); }}
      onLockOut={() => setSession(null)}
    />
  );
}
